import { memo, useState, useCallback } from "react";
import SuffixeRenderer from '../SuffixeRenderer';
import MathText from '../MathText';
import { useQuizTheme, answerTheme, marqueSigne } from './quizTheme';

/**
 * Saisie d'EXPRESSION écrite comme en maths, dans le champ même (décisions
 * prof 2026-09-24) : l'étoile devient ×, « ^2 » devient ². La lettre x n'est
 * jamais touchée : c'est la variable.
 */
const ecrireEnMaths = (texte) => texte.replace(/\*/g, '×').replace(/\^2/g, '²');

/**
 * Composant interactif "Texte libre" côté élève.
 * Affiche un champ de saisie. L'élève tape sa réponse,
 * onAnswer est appelé au blur ou à chaque frappe.
 *
 * @param {string} selectedValue - La réponse déjà saisie (restauration)
 * @param {Function} onAnswer - Callback (valeur) quand l'élève tape
 * @param {?{bonneReponse:string, reponseEleve:string}} [corrige=null] - Correction
 *   SUR PLACE (lot 2, 2026-09-21). N'arrive que si la réponse est fausse (cf.
 *   corrige.jsx) : le champ garde ce qu'il a tapé, en lecture seule et en
 *   `faux`, et la bonne valeur s'écrit dessous en `attendu`. Ici il faut
 *   ÉCRIRE la réponse, elle n'a jamais été à l'écran. Absente : le rendu
 *   d'avant, au bit près.
 * @param {boolean} [expression=false] - La réponse est une EXPRESSION
 *   mathématique (contenu.comparaison posé par le gen, le même signal que la
 *   correction) : le champ l'écrit comme en maths (ecrireEnMaths) et ses
 *   lettres prennent la police italique de KaTeX (classe saisie-maths,
 *   index.css), le même x que dans l'énoncé, en saisie comme dans la
 *   correction sur place. Faux partout ailleurs, en français comme en maths
 *   ordinaires : police normale, le * reste un *.
 */
const TexteLibreEleve = memo(function TexteLibreEleve({ selectedValue, onAnswer, onComplete, suffixe, corrige = null, expression = false }) {
    const clair = useQuizTheme() === 'clair';
    const t = answerTheme(clair);
    const [value, setValue] = useState(selectedValue || '');
    const maths = expression ? ' saisie-maths' : '';

    // Pas de resync sur selectedValue : l'état est lu UNE FOIS au montage,
    // le montage est keyé par question (Quiz / ExerciseurPreview).

    const handleChange = useCallback((e) => {
        const champ = e.target;
        const newVal = expression ? ecrireEnMaths(champ.value) : champ.value;
        setValue(newVal);
        onAnswer(newVal);
        // Complet dès qu'il y a du texte
        onComplete(newVal.trim().length > 0);
        // Le curseur reste derrière ce qui vient d'être tapé (sinon le
        // navigateur le renvoie en fin de champ quand la valeur est réécrite) :
        // sa place est la longueur du début du champ, réécrit lui aussi
        // (« ^2 » → « ² » raccourcit d'un caractère).
        if (newVal !== champ.value) {
            const pos = ecrireEnMaths(champ.value.slice(0, champ.selectionStart)).length;
            requestAnimationFrame(() => champ.setSelectionRange(pos, pos));
        }
    }, [onAnswer, onComplete, expression]);

    if (corrige) {
        const reponse = String(corrige.reponseEleve ?? '').trim();
        return (
            <div className="mt-3 sm:mt-4 flex flex-col gap-3" data-testid="saisie-corrigee">
                {/* Ce qu'il a tapé, tel quel, en lecture seule. Le signe est À CÔTÉ
                    du champ, jamais une rature dedans. */}
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={reponse}
                        readOnly
                        placeholder="Aucune réponse"
                        aria-label="Ta réponse"
                        className={`flex-1 px-4 py-3 sm:py-4 text-lg sm:text-xl font-bold border-2 rounded-xl focus:outline-none ${t.faux}${maths}`}
                    />
                    <SuffixeRenderer suffixe={suffixe} className={`text-lg sm:text-xl font-semibold ${t.suffixe} flex-shrink-0`} />
                    <span
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-500 text-white text-sm font-black flex-shrink-0"
                        aria-label="faux"
                    >
                        {marqueSigne('faux')}
                    </span>
                </div>
                {/* Ce qu'il fallait écrire : rendu comme la feuille le rendait
                    (MathText : KaTeX, milliers), avec la même unité. */}
                <div className="flex flex-col gap-1">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">La réponse</span>
                    <div className="flex items-center gap-2">
                        <div className={`flex-1 px-4 py-3 sm:py-4 text-lg sm:text-xl font-bold border-2 rounded-xl ${t.attendu}`}>
                            <MathText className="[&_.katex]:text-inherit">{String(corrige.bonneReponse ?? '')}</MathText>
                        </div>
                        <SuffixeRenderer suffixe={suffixe} className={`text-lg sm:text-xl font-semibold ${t.suffixe} flex-shrink-0`} />
                        <span className="w-7 flex-shrink-0" aria-hidden="true" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Pas de bandeau d'instruction : un champ de saisie est explicite
                (épure 2026-07-31 — même règle que les QCM, qui n'en ont jamais eu). */}
            {/* Champ de saisie + suffixe */}
            <div className="mt-3 sm:mt-4 flex items-center gap-2">
                <input
                    type="text"
                    value={value}
                    onChange={handleChange}
                    placeholder="Saisir ta réponse..."
                    autoFocus
                    className={`flex-1 px-4 py-3 sm:py-4 text-lg sm:text-xl font-medium border-2 rounded-xl focus:outline-none transition-all ${t.input}${maths}`}
                />
                <SuffixeRenderer suffixe={suffixe} className={`text-lg sm:text-xl font-semibold ${t.suffixe} flex-shrink-0`} />
            </div>
        </>
    );
});

export default TexteLibreEleve;
